import { Plus, X } from "lucide-react";
import { TextField, FieldShell } from "@/components/forms";
import { cn } from "@/utils";
import type { CertificationDraft, CertificationEntry } from "./types";
import type { CertificationField, FieldErrors } from "./validation";

interface CertificationEditorProps {
  certifications: CertificationEntry[];
  draft: CertificationDraft;
  onDraftChange: (field: keyof CertificationDraft, value: string) => void;
  onAdd: () => void;
  onRemove: (id: string) => void;
  error?: string | null;
  /** Messages to show now for the draft's fields (see `visibleErrors`). */
  errors?: FieldErrors<CertificationField>;
  /** Problem with an already-added certification, keyed by its id. */
  entryErrors?: Record<string, string>;
  awardedToPlaceholder?: string;
  formatCertDate: (date: string) => string;
}

export function CertificationEditor({
  certifications,
  draft,
  onDraftChange,
  onAdd,
  onRemove,
  error,
  errors = {},
  entryErrors = {},
  awardedToPlaceholder = "Name",
  formatCertDate,
}: CertificationEditorProps) {
  return (
    <div className="space-y-3">
      <div
        className="space-y-4 rounded-2xl border p-5"
        style={{
          background: "var(--landing-surface)",
          borderColor: "var(--landing-glass-border)",
        }}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="Name"
            required
            value={draft.name}
            onChange={(v) => onDraftChange("name", v)}
            error={errors.name}
            placeholder="e.g. PMP, AWS Solutions Architect"
          />
          <TextField
            label="Issuer"
            required
            value={draft.issuer}
            onChange={(v) => onDraftChange("issuer", v)}
            error={errors.issuer}
            placeholder="Issuing organization"
          />
          <FieldShell label="Issue date" required error={errors.issueDate}>
            <input
              type="date"
              value={draft.issueDate}
              onChange={(e) => onDraftChange("issueDate", e.target.value)}
              aria-invalid={errors.issueDate ? true : undefined}
              className={cn(
                "auth-input h-11 w-full rounded-xl px-3.5 text-sm outline-none",
                errors.issueDate && "!border-destructive/60",
              )}
              style={{ color: "var(--landing-text)" }}
            />
          </FieldShell>
          <FieldShell label="Expiry date" required error={errors.expiryDate}>
            <input
              type="date"
              value={draft.expiryDate}
              onChange={(e) => onDraftChange("expiryDate", e.target.value)}
              aria-invalid={errors.expiryDate ? true : undefined}
              className={cn(
                "auth-input h-11 w-full rounded-xl px-3.5 text-sm outline-none",
                errors.expiryDate && "!border-destructive/60",
              )}
              style={{ color: "var(--landing-text)" }}
            />
          </FieldShell>
          <TextField
            label="Credential ID"
            value={draft.credentialId}
            onChange={(v) => onDraftChange("credentialId", v)}
            error={errors.credentialId}
            placeholder="Optional"
          />
          <TextField
            label="Awarded to"
            value={draft.awardedTo}
            onChange={(v) => onDraftChange("awardedTo", v)}
            error={errors.awardedTo}
            placeholder={awardedToPlaceholder}
          />
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="btn-electric inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-xl text-sm font-semibold"
        >
          <Plus className="h-4 w-4" />
          Add certification
        </button>
      </div>

      {error && <p className="text-xs font-medium text-destructive">{error}</p>}

      {certifications.length > 0 && (
        <div className="space-y-2">
          {certifications.map((cert) => (
            <div
              key={cert.id}
              className={cn(
                "flex items-start justify-between gap-3 rounded-xl border p-3.5",
                entryErrors[cert.id] && "border-destructive/40",
              )}
              style={{
                background: "var(--landing-surface)",
                borderColor: entryErrors[cert.id] ? undefined : "var(--landing-glass-border)",
              }}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold" style={{ color: "var(--landing-text)" }}>
                  {cert.name}
                </p>
                <p className="mt-0.5 text-xs" style={{ color: "var(--landing-text-muted)" }}>
                  {cert.issuer} · {formatCertDate(cert.issueDate)} –{" "}
                  {formatCertDate(cert.expiryDate)}
                </p>
                {entryErrors[cert.id] && (
                  <p className="mt-1 text-xs font-medium text-destructive">{entryErrors[cert.id]}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => onRemove(cert.id)}
                className="rounded-lg p-1.5 transition-colors hover:bg-destructive/10 hover:text-destructive"
                style={{ color: "var(--landing-text-dim)" }}
                aria-label="Remove certification"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
